import { useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, Check, ChevronRight, Clock, Gift, MessageSquare, X } from 'lucide-react';
import '@/pages/GuestDashboard.css';

function SurveyFlow({ survey, language, userName, onClose, onSubmit, preview = false }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(() => survey.questions.map(() => null));
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const setAnswer = (qi, value) => setAnswers(prev => { const next = [...prev]; next[qi] = value; return next; });
  const isRequiredQuestion = (q) => q?.required !== false;
  const allAnswered = answers.every((a, i) => !isRequiredQuestion(survey.questions[i]) || (a !== null && a !== ''));
  const totalQuestions = survey.questions.length;
  const questionIndex = stepIndex - 1;
  const currentQuestion = survey.questions[questionIndex];
  const isIntro = stepIndex === 0;
  const isLastQuestion = questionIndex === totalQuestions - 1;
  const currentAnswer = currentQuestion ? answers[questionIndex] : null;
  const currentQuestionRequired = currentQuestion ? isRequiredQuestion(currentQuestion) : true;
  const canAdvance = currentQuestionRequired ? (currentAnswer !== null && currentAnswer !== '') : true;
  const isOpenQuestion = currentQuestion?.type === 'open'
    || (currentQuestion?.type === 'multiple_choice' && (currentQuestion.options || []).length === 0);

  const goNext = () => {
    if (isIntro && totalQuestions > 0) setStepIndex(1);
    if (!isIntro && canAdvance && !isLastQuestion) setStepIndex(current => current + 1);
  };
  const goBack = () => setStepIndex(current => Math.max(0, current - 1));

  const submit = async () => {
    if (preview) {
      onClose();
      return;
    }

    setSending(true);
    setError('');
    try {
      await onSubmit(survey.id, {
        answers: answers.map((v, i) => ({ question_index: i, value: v })),
      });
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || (language === 'es' ? 'Error al enviar' : 'Error submitting'));
    }
    setSending(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="survey-modal">
      <div className="survey-flow__card bg-surface border border-border rounded-2xl w-full max-w-md modal-card flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        <div className="survey-flow__header px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-bold text-base text-foreground">{survey.title}</h3>
          <button onClick={onClose} className="p-1 text-foreground" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        {isIntro ? (
          <div className="survey-intro" data-testid="survey-intro">
            <div className="survey-intro__visual" aria-hidden="true">
              <BarChart3 className="w-8 h-8" strokeWidth={1.8} />
            </div>
            <div className="survey-intro__content">
              <p className="survey-intro__eyebrow">{language === 'es' ? 'ENCUESTA' : 'SURVEY'}</p>
              <h2 className="survey-intro__title font-heading">{language === 'es' ? 'Tu opinión nos importa' : 'Your opinion matters'}</h2>
              <p className="survey-intro__description">
                {language === 'es'
                  ? 'Completá una encuesta rápida y ayudanos a mejorar.'
                  : 'Complete a quick survey and help us improve.'}
              </p>

              <div className="survey-intro__benefits">
                <div className="survey-intro__benefit survey-intro__benefit--featured">
                  <Gift className="survey-intro__benefit-icon" aria-hidden="true" />
                  <div>
                    <p className="survey-intro__benefit-title">{language === 'es' ? 'Participá en el sorteo de una gran picada' : 'Join the raffle'}</p>
                    <p className="survey-intro__benefit-text">
                      {language === 'es'
                        ? 'Completá la encuesta para participar.'
                        : 'Complete the survey and participate.'}
                    </p>
                  </div>
                </div>
                <div className="survey-intro__benefit">
                  <Clock className="survey-intro__benefit-icon" aria-hidden="true" />
                  <div>
                    <p className="survey-intro__benefit-title">{language === 'es' ? 'Es rápida' : 'It is quick'}</p>
                    <p className="survey-intro__benefit-text">
                      {language === 'es' ? 'Completar la encuesta te llevará solo unos minutos.' : 'It only takes a few minutes to complete.'}
                    </p>
                  </div>
                </div>
                <div className="survey-intro__benefit">
                  <MessageSquare className="survey-intro__benefit-icon" aria-hidden="true" />
                  <div>
                    <p className="survey-intro__benefit-title">{language === 'es' ? 'Tu opinión importa' : 'Your opinion matters'}</p>
                    <p className="survey-intro__benefit-text">
                      {language === 'es' ? 'Tus respuestas nos ayudan a mejorar la experiencia en la residencia.' : 'Your answers help us improve the experience in the residence.'}
                    </p>
                  </div>
                </div>
              </div>

              <button type="button" onClick={goNext} disabled={totalQuestions === 0} className="survey-intro__start" data-testid="survey-begin">
                {language === 'es' ? 'Comenzar encuesta' : 'Start survey'} <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : submitted ? (
          <div className="survey-thanks" data-testid="survey-thanks">
            <div className="survey-thanks__icon" aria-hidden="true">
              <Check className="w-9 h-9" strokeWidth={2.2} />
            </div>
            <h2 className="survey-thanks__title font-heading">
              {language === 'es' ? '¡Gracias por responder!' : 'Thanks for responding!'}
            </h2>
            <p className="survey-thanks__description">
              {language === 'es' ? 'Tu opinión nos ayuda a seguir mejorando WannaColive.' : 'Your opinion helps us keep improving WannaColive.'}
            </p>
            <div className="survey-thanks__raffle">
              <Gift className="survey-thanks__raffle-icon" aria-hidden="true" />
              <div>
                <p className="survey-thanks__raffle-title">{language === 'es' ? 'Ya estás participando en el sorteo' : 'You are now participating in the raffle'}</p>
                <p className="survey-thanks__raffle-text">{language === 'es' ? 'Pronto anunciaremos los ganadores en la app. ¡Mucha suerte!' : 'We will announce the winners in the app soon. Good luck!'}</p>
              </div>
            </div>
            <button onClick={onClose} className="survey-thanks__home-button" data-testid="survey-back-home">
              {language === 'es' ? 'Volver al inicio' : 'Back to home'} <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <>
            <div className="survey-flow__question flex-1 overflow-y-auto px-5 py-4">
              <div className="survey-flow__progress" aria-label={`${questionIndex + 1} ${language === 'es' ? 'de' : 'of'} ${totalQuestions}`}>
                <span>{questionIndex + 1} {language === 'es' ? 'de' : 'of'} {totalQuestions}</span>
                <div className="survey-flow__progress-dots" aria-hidden="true">
                  {survey.questions.map((_, index) => <span key={index} className={index <= questionIndex ? 'is-active' : ''} />)}
                </div>
              </div>
              {currentQuestion && <div className="survey-flow__question-content">
                  <p className={`survey-flow__question-title${currentQuestion.type === 'rating' ? ' survey-flow__question-title--rating' : ''}`}>{currentQuestion.text}{!currentQuestionRequired && <span className="survey-flow__optional-badge">{language === 'es' ? 'Opcional' : 'Optional'}</span>}</p>
                  {currentQuestion.type === 'rating' ? (
                    <>
                    <div className="survey-flow__rating-scale" data-scale={currentQuestion.scale || 10}>
                      {Array.from({ length: currentQuestion.scale || 10 }, (_, i) => i + 1).map(n => (
                        <button key={n} type="button" onClick={() => setAnswer(questionIndex, n)}
                          className={`w-8 h-8 rounded-lg border text-sm font-semibold transition-colors ${answers[questionIndex] === n ? 'bg-foreground text-background border-foreground' : 'bg-surface text-foreground border-border hover:bg-muted'}`}
                          data-testid={`survey-rating-${questionIndex}-${n}`}>{n}</button>
                      ))}
                    </div>
                    <div className="survey-flow__rating-labels" data-scale={currentQuestion.scale || 10} aria-hidden="true">
                      <span>{language === 'es' ? 'Muy insatisfecho' : 'Very dissatisfied'}</span>
                      <span>{language === 'es' ? 'Muy satisfecho' : 'Very satisfied'}</span>
                    </div>
                    </>
                  ) : currentQuestion.type === 'multiple_choice' && (currentQuestion.options || []).length > 0 ? (
                    <div className="flex flex-col gap-1.5 mt-2">
                      {(currentQuestion.options || []).map(opt => (
                        <button key={opt} type="button" onClick={() => setAnswer(questionIndex, opt)}
                          className={`px-3 py-2 text-left rounded-lg border text-sm font-medium transition-colors ${answers[questionIndex] === opt ? 'bg-foreground text-background border-foreground' : 'bg-surface text-foreground border-border hover:bg-muted'}`}
                          data-testid={`survey-option-${questionIndex}`}>{opt}</button>
                      ))}
                    </div>
                  ) : isOpenQuestion ? (
                    <textarea value={answers[questionIndex] || ''} onChange={(e) => setAnswer(questionIndex, e.target.value)}
                      className="input-brutal w-full px-3 py-2 mt-2 text-sm min-h-[100px] resize-none" rows={4}
                      placeholder={language === 'es' ? 'Escribí tu respuesta…' : 'Type your answer…'}
                      data-testid={`survey-open-answer-${questionIndex}`} />
                  ) : null}
                </div>}
             </div>
             <div className="survey-flow__actions px-5 py-3 border-t border-border">
              {error && <p className="text-xs text-destructive mb-2">{error}</p>}
              <div className="flex gap-2">
              <button type="button" onClick={goBack} disabled={stepIndex === 1 || sending} className="survey-flow__back flex-1 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-35" data-testid="survey-back">{language === 'es' ? 'Volver' : 'Back'}</button>
              {!isLastQuestion && <button type="button" onClick={goNext} disabled={!canAdvance || sending} className="survey-flow__next w-full py-2.5 rounded-lg font-semibold text-sm disabled:opacity-40" data-testid="survey-next">{language === 'es' ? 'Siguiente' : 'Next'} <ChevronRight className="w-4 h-4" aria-hidden="true" /></button>}
              {isLastQuestion && <button onClick={submit} disabled={preview ? false : !allAnswered || sending} className="survey-flow__next w-full py-2.5 rounded-lg font-semibold text-sm disabled:opacity-40" data-testid="survey-submit">
                {preview ? (language === 'es' ? 'Finalizar vista previa' : 'Finish preview') : (sending ? (language === 'es' ? 'Enviando…' : 'Sending…') : (language === 'es' ? 'Enviar y participar' : 'Submit & enter raffle'))}
              </button>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}


export default SurveyFlow;
